import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../../context/AuthContext';
import useCompanyLocations from '../../../locations/hooks/useCompanyLocations';
import { normalizeLocationIds } from '../../../locations/locationTypes';
import {
  buildNextEmployeeIdsForSelfToggle,
  getEligibleServicesForEmployee,
  getServicesAssignedToEmployee,
  isSelfServiceAssignmentEnabled,
} from './employeeSelfServiceAssignmentUtils';
import { normalizeEmployeeIds } from './serviceStaffAssignmentUtils';
import { getEmployee, listServices, upsertService } from '../../../api/companyRecords';

const useEmployeeSelfServiceAssignment = ({
  employee,
  canSelfManage = false,
}) => {
  const { companyData } = useAuth();
  const companyId = companyData?.id;
  const employeeId = employee?.id;
  const { activeLocations } = useCompanyLocations({
    companyId,
    companyData,
    activeOnly: true,
  });

  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingServiceId, setSavingServiceId] = useState('');
  const [error, setError] = useState('');
  const [resolvedEmployee, setResolvedEmployee] = useState(employee);

  useEffect(() => {
    setResolvedEmployee(employee);
  }, [employee]);

  useEffect(() => {
    let cancelled = false;

    const hydrateEmployeeLocations = async () => {
      if (!employeeId || !companyId) {
        return;
      }

      const existingLocationIds = normalizeLocationIds(employee?.locationIds);
      if (existingLocationIds.length > 0) {
        return;
      }

      try {
        const employeeData = await getEmployee({ companyId, id: employeeId });
        if (cancelled || !employeeData) {
          return;
        }

        setResolvedEmployee((current) => ({
          ...(current || {}),
          ...employeeData,
          id: employeeId,
          locationIds: normalizeLocationIds(employeeData.locationIds),
        }));
      } catch (hydrateError) {
        console.error('Error loading employee locations for self-assignment:', hydrateError);
      }
    };

    hydrateEmployeeLocations();

    return () => {
      cancelled = true;
    };
  }, [employee, employeeId, companyId]);

  const selfServiceEnabled = isSelfServiceAssignmentEnabled(companyData);
  const canEdit = Boolean(canSelfManage && selfServiceEnabled && employeeId && companyId);

  useEffect(() => {
    if (!companyId) {
      setServices([]);
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    const load = async () => {
      try {
        const nextServices = await listServices({ companyId });
        if (!cancelled) {
          setServices(nextServices);
          setLoading(false);
        }
      } catch (snapshotError) {
        console.error('Error loading services for self-assignment:', snapshotError);
        if (!cancelled) {
          setError('Failed to load services.');
          setLoading(false);
        }
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [companyId]);

  const assignedServices = useMemo(
    () => getServicesAssignedToEmployee(services, employeeId),
    [services, employeeId]
  );

  const eligibleServices = useMemo(
    () =>
      getEligibleServicesForEmployee({
        services,
        employee: resolvedEmployee,
        locations: activeLocations,
      }),
    [services, resolvedEmployee, activeLocations]
  );

  const displayServices = useMemo(() => {
    if (canEdit) {
      return [...eligibleServices].sort((a, b) =>
        String(a?.name || '').localeCompare(String(b?.name || ''), undefined, { sensitivity: 'base' })
      );
    }

    return [...assignedServices].sort((a, b) =>
      String(a?.name || '').localeCompare(String(b?.name || ''), undefined, { sensitivity: 'base' })
    );
  }, [canEdit, eligibleServices, assignedServices]);

  const isAssignedToService = useCallback(
    (service) => normalizeEmployeeIds(service?.employeeIds).includes(String(employeeId || '').trim()),
    [employeeId]
  );

  const toggleAssignment = useCallback(
    async (service, nextAssigned) => {
      if (!canEdit || !service?.id || !companyId) {
        return false;
      }

      const nextEmployeeIds = buildNextEmployeeIdsForSelfToggle({
        currentEmployeeIds: service.employeeIds,
        employeeId,
        assigned: nextAssigned,
      });

      setSavingServiceId(service.id);
      setError('');

      try {
        await upsertService({
          companyId,
          id: service.id,
          service: {
            employeeIds: nextEmployeeIds,
            updatedAt: new Date().toISOString(),
          },
        });
        setServices((current) => current.map((entry) => (
          entry.id === service.id ? { ...entry, employeeIds: nextEmployeeIds } : entry
        )));
        return true;
      } catch (toggleError) {
        console.error('Failed to update service assignment:', toggleError);
        setError('Could not update your service assignment. Please try again.');
        return false;
      } finally {
        setSavingServiceId('');
      }
    },
    [canEdit, companyId, employeeId]
  );

  return {
    loading,
    error,
    selfServiceEnabled,
    canEdit,
    displayServices,
    assignedServices,
    eligibleServices,
    savingServiceId,
    isAssignedToService,
    toggleAssignment,
  };
};

export default useEmployeeSelfServiceAssignment;
